// static/table_engine_src/core/selection.js

import { escCss } from "../util/dom.js";
import { installSelectionHistory } from "./selection/history.js";
import { installSelectionKeyboard } from "./selection/keyboard.js";
import { createSelectionNav } from "./selection/nav.js";
import { installSelectionVisuals } from "./selection/visuals.js";

// Module-scoped: last active TableEngine ctx (across multiple tables on a page)
let __teLastActiveCtx = null;

export function installSelection(ctx) {
  if (!ctx || typeof ctx !== "object") return;

  const { table, thead, tbody, cols } = ctx;
  if (!table || !thead || !tbody || !Array.isArray(cols)) return;

  const on =
    typeof ctx.on === "function"
      ? ctx.on
      : (target, type, handler, options) => {
          if (!target || typeof target.addEventListener !== "function") return handler;
          if (typeof handler !== "function") return handler;
          target.addEventListener(type, handler, options);
          return handler;
        };

  // Make table focusable (required for stable NAV-mode focus)
  try {
    if (!table.hasAttribute("tabindex")) table.tabIndex = 0;
  } catch {}

  ctx._sel =
    ctx._sel ||
    {
      selectedCols: new Set(),
      selectedRows: new Set(),
      colAnchorIdx: null,
      rowAnchorIdx: null,
      cellAnchor: null,
      cellFocus: null,
      cellRange: null,
      cellRanges: [],
    };
  if (!ctx._sel || typeof ctx._sel !== "object") ctx._sel = {};
  if (!("cellFocus" in ctx._sel)) ctx._sel.cellFocus = null;
  if (!Array.isArray(ctx._sel.cellRanges)) ctx._sel.cellRanges = [];

  let _isActiveTable = false;

  function setActiveUI() {
    try {
      if (typeof ctx.setInactive === "function") ctx.setInactive(false);
    } catch {}

    try {
      document.body.classList.remove("te-inactive");
    } catch {}
  }

  function markActiveTable() {
    _isActiveTable = true;
    __teLastActiveCtx = ctx;
    setActiveUI();
  }

  function maybeDeactivateFromEventTarget(t) {
    if (!t) return;
    if (!table.contains(t)) _isActiveTable = false;
  }

  let _liveRegion = null;
  let _lastAnnouncement = "";

  function ensureLiveRegion() {
    if (_liveRegion && _liveRegion.isConnected) return _liveRegion;
    const host = table.parentElement || table;
    if (!host) return null;

    let region = host.querySelector?.('[data-te-live-region="1"]') || null;
    if (!region) {
      region = document.createElement("div");
      region.dataset.teLiveRegion = "1";
      region.dataset.teOwned = "1";
      region.setAttribute("aria-live", "polite");
      region.setAttribute("aria-atomic", "true");
      region.style.position = "absolute";
      region.style.left = "-9999px";
      region.style.top = "0";
      region.style.width = "1px";
      region.style.height = "1px";
      region.style.overflow = "hidden";
      region.style.whiteSpace = "nowrap";
      host.appendChild(region);
    }
    _liveRegion = region;
    return region;
  }

  function announceSelectionA11y(msg) {
    const text = String(msg || "").trim();
    if (!text) return;
    const region = ensureLiveRegion();
    if (!region) return;

    if (text === _lastAnnouncement) region.textContent = "";
    _lastAnnouncement = text;

    const run = () => {
      try {
        region.textContent = text;
      } catch {}
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    else setTimeout(run, 0);
  }

  ctx.announceSelectionA11y = announceSelectionA11y;

  on(table, "pointerdown", markActiveTable, true);
  on(table, "focusin", markActiveTable, true);

  on(document, "pointerdown", (e) => maybeDeactivateFromEventTarget(e.target), true);
  on(document, "focusin", (e) => maybeDeactivateFromEventTarget(e.target), true);
  ctx.addCleanup?.(() => {
    if (ctx.announceSelectionA11y === announceSelectionA11y) {
      try { delete ctx.announceSelectionA11y; } catch {}
    }
    if (_liveRegion && _liveRegion.dataset?.teOwned === "1") {
      try { _liveRegion.remove(); } catch {}
    }
    if (__teLastActiveCtx === ctx) __teLastActiveCtx = null;
  });

  function rowEls() {
    try {
      if (typeof ctx.getRowEls === "function") return ctx.getRowEls() || [];
    } catch {}
    return [...tbody.querySelectorAll("tr")];
  }

  function colIndexByKey(colKey) {
    if (typeof ctx.colIndexByKey === "function") return ctx.colIndexByKey(colKey);
    for (let i = 0; i < cols.length; i++) if (cols[i]?.key === colKey) return i;
    return -1;
  }

  function keyForColIndex(cIdx) {
    const c = cols[cIdx];
    return c ? c.key : null;
  }

  function thForKey(colKey) {
    return thead.querySelector(`th[data-col-key="${escCss(colKey)}"]`);
  }

  function tdAt(tr, cIdx) {
    if (!tr) return null;
    const td = tr.querySelector(`td[data-col-idx="${String(cIdx)}"]`);
    if (td) return td;
    return tr.children?.[cIdx] || null;
  }

  function focusNavHard() {
    const run = () => {
      setActiveUI();

      try {
        applySelectionVisuals();
      } catch {}

      const navCell =
        table.querySelector?.('[data-te-nav-cell="1"][tabindex="0"]') ||
        table.querySelector?.('[data-te-nav-cell="1"]');
      if (navCell && typeof navCell.focus === "function") {
        try {
          navCell.focus({ preventScroll: true });
          return;
        } catch {}
        try {
          navCell.focus();
          return;
        } catch {}
      }

      try {
        if (!table.hasAttribute("tabindex") || table.tabIndex < 0) table.tabIndex = 0;
      } catch {}

      try {
        table.focus({ preventScroll: true });
      } catch {
        try {
          table.focus();
        } catch {}
      }
    };

    if (typeof queueMicrotask === "function") queueMicrotask(run);
    else Promise.resolve().then(run);

    try {
      requestAnimationFrame(() => run());
    } catch {}

    setTimeout(run, 0);
  }

  function scrollCellIntoView(r, c) {
    try {
      ctx.ensureRowVisible?.(r);
    } catch {}

    const tr = rowEls()[r];
    const td = tr ? tdAt(tr, c) : null;
    if (!td) return;
    try {
      td.scrollIntoView({ block: "nearest", inline: "nearest" });
    } catch {}
  }

  function activeEditorEl() {
    const ae = document.activeElement;
    if (!ae) return null;
    if (!table.contains(ae)) return null;
    if (!ae.matches?.("input, textarea, select")) return null;
    if (!ae.closest?.("tbody")) return null;
    return ae;
  }

  function focusEditorAtCell(r, c, { selectAll = true } = {}) {
    try {
      ctx.ensureRowVisible?.(r);
    } catch {}

    const tr = rowEls()[r];
    const td = tr ? tdAt(tr, c) : null;
    if (!td) return false;

    const el = td.querySelector("input, textarea, select");
    if (!el) return false;
    if (el.disabled) return false;

    markActiveTable();

    try {
      el.focus({ preventScroll: true });
    } catch {}
    if (selectAll && el.tagName === "INPUT") {
      try {
        el.select?.();
      } catch {}
    }
    return true;
  }

  const nav = createSelectionNav(ctx, { cols, rowEls, tdAt });

  const visuals = installSelectionVisuals(ctx, {
    table,
    cols,
    rowEls,
    colIndexByKey,
    keyForColIndex,
    thForKey,
    tdAt,
    clampRC: nav.clampRC,
    scrollCellIntoView,
  });

  const {
    applySelectionVisuals,
    clearSelectionVisuals,
    setSelectedColumns,
    setSelectedRows,
    setCellAnchor,
    setCellRangeFromAnchor,
    selectColumnByClick,
    selectRowByClick,
    getSelection,
    getSelections,
    getPasteStart,
  } = visuals;

  function exitEditMode({ keepAnchor = true } = {}) {
    const ae = activeEditorEl();
    if (!ae) return;

    let r = null;
    let c = null;
    if (keepAnchor) {
      const td = ae.closest?.("td[data-col-idx]");
      const tr = ae.closest?.("tr");
      r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
      c = parseInt(td?.dataset?.colIdx || "-1", 10);
      if (!(Number.isFinite(r) && r >= 0)) r = null;
      if (!(Number.isFinite(c) && c >= 0)) c = null;
    }

    const run = () => {
      try {
        ae.blur();
      } catch {}

      if (keepAnchor && r != null && c != null) {
        setCellAnchor(r, c);
      }

      focusNavHard();
    };

    if (typeof queueMicrotask === "function") queueMicrotask(run);
    else Promise.resolve().then(run);

    try {
      requestAnimationFrame(run);
    } catch {}
    setTimeout(run, 0);
  }

  const history = installSelectionHistory(ctx, {
    on,
    tbody,
    cols,
    rowEls,
    tdAt,
    keyForColIndex,
    isHiddenOrNonDataCol: nav.isHiddenOrNonDataCol,
    getSelection,
    setCellAnchor,
    firstNavigableCol: nav.firstNavigableCol,
    focusEditorAtCell,
    focusNavHard,
    markActiveTable,
  });

  const keyboard = installSelectionKeyboard(ctx, {
    on,
    tbody,
    isActiveTable: () => _isActiveTable,
    isLastActiveCtx: () => __teLastActiveCtx === ctx,
    markActiveTable,
    activeEditorEl,
    focusNavHard,
    focusEditorAtCell,
    rowEls,
    nextNavigableCol: nav.nextNavigableCol,
    firstNavigableCol: nav.firstNavigableCol,
    lastNavigableCol: nav.lastNavigableCol,
    nearestNavigableCol: nav.nearestNavigableCol,
    cellHasValue: nav.cellHasValue,
    setCellAnchor,
    setCellRangeFromAnchor,
    setSelectedColumns,
    setSelectedRows,
    getSelection,
    exitEditMode,
    editorRC: history.editorRC,
    wireHistory: history.wireHistory,
    wireBulkHistoryHooks: history.wireBulkHistoryHooks,
    scrollCellIntoView,
  });

  ctx.applySelectionVisuals = applySelectionVisuals;
  ctx.clearSelectionVisuals = clearSelectionVisuals;

  ctx.selectColumnByClick = selectColumnByClick;
  ctx.selectRowByClick = selectRowByClick;

  ctx.setSelectedColumns = setSelectedColumns;
  ctx.setSelectedRows = setSelectedRows;

  ctx.setCellAnchor = setCellAnchor;
  ctx.setCellRangeFromAnchor = setCellRangeFromAnchor;

  ctx.getSelection = getSelection;
  ctx.getSelections = getSelections;
  ctx.getPasteStart = getPasteStart;

  ctx.wireKeyboard = keyboard.wireKeyboard;
  ctx.wireMouseExcelish = keyboard.wireMouseExcelish;
}
